import { useState, useEffect } from 'react';
import { TrendingUp, FileText, Target, BookOpen, Activity, BarChart, Bell, Link, Loader, Plus, Users, Calendar, Award, CheckCircle, AlertCircle } from 'lucide-react';
import { listLearningPaths, getGapsAnalysis, getModules, getProgress, getDashboard, getAlerts, exportReport, integrateRecommendations } from '../services/learningPaths';

export default function Tapis() {
  const [activeTab, setActiveTab] = useState('plans');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const tabs = [
    { id: 'plans', label: 'Plans de Formation', icon: FileText },
    { id: 'gaps', label: 'Objectifs & Écarts', icon: Target },
    { id: 'modules', label: 'Modules', icon: BookOpen },
    { id: 'progress', label: 'Progression', icon: Activity },
    { id: 'dashboard', label: 'Dashboard RH', icon: BarChart },
    { id: 'alerts', label: 'Alertes', icon: Bell }
  ];

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let result;
      switch (activeTab) {
        case 'plans':
          result = await listLearningPaths();
          break;
        case 'gaps':
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          result = await getGapsAnalysis(user.id || 1);
          break;
        case 'modules':
          result = await getModules();
          break;
        case 'progress':
          result = await getProgress(1); // pathId = 1
          break;
        case 'dashboard':
          result = await getDashboard();
          break;
        case 'alerts':
          result = await getAlerts();
          break;
      }
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      );
    }

    switch (activeTab) {
      case 'plans':
        return renderPlans();
      case 'gaps':
        return renderGaps();
      case 'modules':
        return renderModules();
      case 'progress':
        return renderProgress();
      case 'dashboard':
        return renderDashboard();
      case 'alerts':
        return renderAlerts();
      default:
        return null;
    }
  };

  const renderPlans = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Plans de formation</h2>
        <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nouveau plan
        </button>
      </div>

      {data?.paths?.map((path) => (
        <div key={path.id} className="border border-gray-200 rounded-lg p-5 hover:border-indigo-300 transition-all">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">{path.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{path.description}</p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  path.type === 'individual' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {path.type === 'individual' ? 'Individuel' : 'Collectif'}
                </span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  path.status === 'active' ? 'bg-green-100 text-green-700' :
                  path.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                  'bg-orange-100 text-orange-700'
                }`}>
                  {path.status === 'active' ? 'Actif' : path.status === 'completed' ? 'Terminé' : 'Brouillon'}
                </span>
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {path.usersCount} participant{path.usersCount > 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-indigo-600 mb-1">{path.progress}%</div>
              <div className="text-xs text-gray-500">Progression</div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
              style={{ width: `${path.progress}%` }}
            />
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {path.startDate} → {path.endDate}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGaps = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-600">{data?.analysis?.currentCompetencesCount}</div>
            <div className="text-sm text-gray-600">Compétences actuelles</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{data?.analysis?.targetCompetencesCount}</div>
            <div className="text-sm text-gray-600">Compétences cibles</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{data?.analysis?.summary?.high}</div>
            <div className="text-sm text-gray-600">Écarts importants</div>
          </div>
        </div>
      </div>

      <h3 className="font-semibold text-gray-900">Analyse des écarts</h3>
      <div className="space-y-3">
        {data?.analysis?.gaps?.map((gap, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">{gap.label}</span>
              <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                gap.gap === 'high' ? 'bg-red-100 text-red-700' :
                gap.gap === 'medium' ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`}>
                Écart {gap.gap === 'high' ? 'élevé' : gap.gap === 'medium' ? 'moyen' : 'faible'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>Niveau actuel: <span className="font-medium">{gap.current}</span></span>
              <span>→</span>
              <span>Objectif: <span className="font-medium">{gap.level}</span></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderModules = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data?.modules?.map((module) => (
        <div key={module.id} className="border border-gray-200 rounded-lg p-5 hover:border-indigo-300 hover:shadow-sm transition-all">
          <div className="flex items-start justify-between mb-3">
            <BookOpen className="w-8 h-8 text-indigo-600" />
            <span className={`px-2 py-1 text-xs rounded-full ${
              module.source === 'internal' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
            }`}>
              {module.source === 'internal' ? 'Interne' : 'Externe'}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">{module.title}</h3>
          <p className="text-sm text-gray-600 mb-3">{module.description}</p>
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">{module.category}</span>
            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">{module.duration}h</span>
            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">{module.level}</span>
          </div>
          <div className="mt-3 text-xs text-gray-500">{module.provider}</div>
        </div>
      ))}
    </div>
  );

  const renderProgress = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{data?.progress?.pathTitle}</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{data?.progress?.overallProgress}%</div>
            <div className="text-sm text-gray-600">Progression globale</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{data?.progress?.users?.[0]?.completedModules}</div>
            <div className="text-sm text-gray-600">Modules terminés</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{data?.progress?.users?.[0]?.timeSpent}h</div>
            <div className="text-sm text-gray-600">Temps passé</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-600">{data?.progress?.users?.[0]?.totalModules}</div>
            <div className="text-sm text-gray-600">Modules total</div>
          </div>
        </div>
      </div>

      <h3 className="font-semibold text-gray-900">Modules du parcours</h3>
      <div className="space-y-3">
        {data?.progress?.modules?.map((module) => (
          <div key={module.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">{module.title}</span>
              <span className={`px-3 py-1 text-xs rounded-full font-medium flex items-center gap-1 ${
                module.status === 'completed' ? 'bg-green-100 text-green-700' :
                module.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {module.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                {module.status === 'completed' ? 'Terminé' : module.status === 'in_progress' ? 'En cours' : 'Non commencé'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className={`h-full rounded-full ${
                  module.status === 'completed' ? 'bg-green-500' :
                  module.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-300'
                }`}
                style={{ width: `${module.progress}%` }}
              />
            </div>
            <div className="text-sm text-gray-600">
              Progression: {module.progress}%
              {module.validatedAt && ` • Validé le ${module.validatedAt}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Parcours actifs', value: data?.dashboard?.overview?.activePaths, color: 'blue' },
          { label: 'Participants', value: data?.dashboard?.overview?.activeUsers, color: 'green' },
          { label: 'Progression moy.', value: `${data?.dashboard?.overview?.averageProgress}%`, color: 'purple' },
          { label: 'Total parcours', value: data?.dashboard?.overview?.totalPaths, color: 'indigo' },
          { label: 'Terminés', value: data?.dashboard?.overview?.completedPaths, color: 'emerald' },
          { label: 'Total users', value: data?.dashboard?.overview?.totalUsers, color: 'cyan' }
        ].map((stat, index) => (
          <div key={index} className={`bg-gradient-to-br from-${stat.color}-50 to-${stat.color}-100 rounded-lg p-4 border border-${stat.color}-200`}>
            <div className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</div>
            <div className="text-xs text-gray-600">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold text-gray-900 mb-4">Par département</h3>
          <div className="space-y-3">
            {data?.dashboard?.byDepartment?.map((dept, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{dept.department}</span>
                  <span className="text-sm font-semibold text-indigo-600">{dept.avgProgress}%</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{dept.activePaths} parcours actifs</span>
                  <span>•</span>
                  <span>{dept.users} utilisateurs</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-4">Activité récente</h3>
          <div className="space-y-3">
            {data?.dashboard?.recentActivity?.map((activity, index) => (
              <div key={index} className="border-l-4 border-green-500 bg-green-50 p-3 rounded">
                <div className="font-medium text-gray-900 text-sm">{activity.user}</div>
                <div className="text-xs text-gray-600">{activity.action}</div>
                <div className="text-xs text-gray-500 mt-1">{activity.date}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAlerts = () => (
    <div className="space-y-3">
      {data?.alerts?.map((alert) => (
        <div
          key={alert.id}
          className={`border-l-4 p-4 rounded-lg ${
            alert.severity === 'high' ? 'border-red-500 bg-red-50' :
            alert.severity === 'success' ? 'border-green-500 bg-green-50' :
            'border-orange-500 bg-orange-50'
          }`}
        >
          <div className="flex items-start gap-3">
            {alert.severity === 'high' ? <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" /> :
             alert.severity === 'success' ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /> :
             <Bell className="w-5 h-5 text-orange-600 flex-shrink-0" />}
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">{alert.title}</h3>
              <p className="text-sm text-gray-700">{alert.message}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                <span>{alert.usersAffected} utilisateur{alert.usersAffected > 1 ? 's' : ''}</span>
                <span>•</span>
                <span>{new Date(alert.date).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-lg">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Tapis</h1>
              <p className="text-gray-600">Plan de montée en compétences & Trajectoires RH</p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
